import React, { useCallback, useEffect, useState } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRight, LogOut } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AxiomFlowMark } from './shared/BrandLogo';
import { ContactCard, CONTACT_EMAIL, mailtoHref } from './landing/ContactCard';

/**
 * /abf (also /oms-marketing/abf) — the asset-based finance entry page.
 *
 * Unlike the home page's fixed-viewport beat machine this is a native-scroll page: an ABF
 * operations lead scans it, and an outreach email can deep-link a section (#base, #cash).
 * Same brand as the home page (Fraunces, Inter, JetBrains Mono; dark and paper; one mint).
 * Every figure is from the Ridgeline sample facility as the product recorded it.
 */

/* ------------------------------------------------------------- palette (mirrors OMSLandingPage) */
const MINT = '#54e6c6';
const MINT_INK = '#18664f';           // the accent on paper, where mint has no contrast
const CAL_URL = 'https://cal.com/srinivas-chuthari-f3beys/founder-introduction';
const DEMO_BOOKING_ENABLED = process.env.REACT_APP_DEMO_BOOKING_ENABLED === 'true';
// "Talk to us" opens the enquiry form (ContactCard, posts to /api/public/enquiries); the mailto on the
// same links is the fallback for a reader with scripting off, and the subject becomes the enquiry's topic.
const CONTACT_SUBJECT = 'AxiomFlow ABF: walk a facility with us';
const CONTACT = mailtoHref(CONTACT_SUBJECT);
const HOME = '/';
const T = {
  dark:  { bg: '#08080a', ink: '#f4efe7', sub: 'rgba(244,239,231,0.62)', line: 'rgba(244,239,231,0.13)', card: 'rgba(244,239,231,0.035)' },
  light: { bg: '#efe9dc', ink: '#16150f', sub: 'rgba(22,21,15,0.66)',    line: 'rgba(22,21,15,0.14)',    card: 'rgba(22,21,15,0.04)' }
} as const;
type Mode = keyof typeof T;
const EASE = [0.22, 1, 0.36, 1] as const;

/* ------------------------------------------------------------- shells */
const Global = createGlobalStyle` html, body { background: #08080a; } `;
const Root = styled.div` position: relative; z-index: 1; font-family: 'Inter', sans-serif; background: ${T.dark.bg}; color: ${T.dark.ink}; `;
const Band = styled.section<{ $m: Mode }>`
  background: ${p => T[p.$m].bg}; color: ${p => T[p.$m].ink};
  padding: clamp(56px, 8vw, 96px) clamp(20px, 6vw, 96px);
  scroll-margin-top: 24px;
`;
const Inner = styled.div` width: min(1280px, 100%); margin: 0 auto; display: grid; gap: clamp(28px, 4vw, 44px); `;
const Two = styled.div<{ $ratio?: string; $align?: string }>`
  display: grid; grid-template-columns: ${p => p.$ratio || '1fr 1fr'}; gap: clamp(28px, 5vw, 64px); align-items: ${p => p.$align || 'center'};
  @media (max-width: 860px) { grid-template-columns: 1fr; }
`;
const Stack = styled.div<{ $gap?: number }>` display: grid; gap: ${p => p.$gap ?? 18}px; `;

/* ------------------------------------------------------------- type */
const Eyebrow = styled.div`
  font-family: 'JetBrains Mono', monospace; font-size: 11px; letter-spacing: 0.18em; text-transform: uppercase; opacity: 0.55;
`;
const H1 = styled.h1`
  font-family: 'Fraunces', Georgia, serif; font-weight: 300; letter-spacing: -0.014em; line-height: 1.03; margin: 0;
  font-size: clamp(2.4rem, 5.4vw, 4.1rem);
  .mint { color: ${MINT}; }
`;
const H2 = styled.h2<{ $m?: Mode }>`
  font-family: 'Fraunces', Georgia, serif; font-weight: 300; letter-spacing: -0.012em; line-height: 1.05; margin: 0;
  font-size: clamp(1.9rem, 3.6vw, 2.9rem);
  .mint { color: ${p => (p.$m === 'light' ? MINT_INK : MINT)}; }
`;
const Lede = styled.p<{ $m: Mode }>`
  margin: 0; font-size: clamp(1rem, 1.3vw, 1.08rem); line-height: 1.62; color: ${p => T[p.$m].sub}; max-width: 48ch;
`;
const Fine = styled.div` font-family: 'JetBrains Mono', monospace; font-size: 10px; letter-spacing: 0.1em; text-transform: uppercase; opacity: 0.42; line-height: 1.6; `;

/* ------------------------------------------------------------- controls */
const CtaRow = styled.div` display: flex; gap: 12px; flex-wrap: wrap; align-items: center; `;
const Primary = styled.a`
  display: inline-flex; align-items: center; gap: 9px; min-height: 46px; padding: 0 24px; border-radius: 999px; text-decoration: none; border: 0; cursor: pointer;
  background: ${MINT}; color: #04120e; font: 700 14px 'Inter', sans-serif; transition: transform .2s ease, box-shadow .3s ease;
  &:hover { transform: translateY(-2px); box-shadow: 0 16px 44px ${MINT}44; }
`;
const Ghost = styled.a<{ $m?: Mode }>`
  display: inline-flex; align-items: center; gap: 7px; min-height: 46px; padding: 0 22px; border-radius: 999px; text-decoration: none; cursor: pointer;
  border: 1px solid ${p => (p.$m === 'light' ? 'rgba(22,21,15,0.3)' : 'rgba(244,239,231,0.26)')}; background: transparent; color: inherit; opacity: 0.82;
  font: 600 14px 'Inter', sans-serif; transition: opacity .2s, border-color .2s;
  &:hover { opacity: 1; border-color: currentColor; }
`;
const Header = styled.header`
  display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 22px clamp(20px, 5vw, 46px);
`;
const Brand = styled.a` display: inline-flex; align-items: center; gap: 10px; color: inherit; text-decoration: none;
  span { font: 600 17px 'Fraunces', serif; letter-spacing: -0.01em; white-space: nowrap; } `;
const TopRight = styled.div` display: inline-flex; align-items: center; gap: 14px;
  button { border: 0; background: transparent; color: inherit; cursor: pointer; opacity: 0.62; font: 600 13px 'Inter', sans-serif; display: inline-flex; align-items: center; gap: 6px; }
  button:hover { opacity: 1; } `;
const QuietSm = styled.button`
  && { display: inline-flex; align-items: center; cursor: pointer; border-radius: 999px; border: 1px solid; background: transparent; color: inherit;
    font: 600 13px 'Inter', sans-serif; padding: 8px 15px; opacity: 0.55; transition: opacity .2s; white-space: nowrap; }
  &&:hover { opacity: 1; }
`;
const DemoSm = styled.a`
  display: inline-flex; align-items: center; gap: 7px; text-decoration: none; border-radius: 999px; background: ${MINT}; color: #04120e;
  font: 700 13px 'Inter', sans-serif; padding: 9px 16px; white-space: nowrap;
`;
const OnPage = styled.nav`
  display: grid; gap: 8px; font-size: 13px; line-height: 1.5;
  a { color: inherit; text-decoration: none; opacity: 0.78; display: inline-flex; gap: 10px; align-items: baseline; }
  a:hover { opacity: 1; }
  a b { font-family: 'JetBrains Mono', monospace; font-weight: 500; font-size: 10px; opacity: 0.5; }
  @media (max-width: 860px) { display: none; }
`;

/* ------------------------------------------------------------- the facility header, as the product shows it */
const Facility = styled.div`
  border: 1px solid ${T.dark.line}; background: rgba(244,239,231,0.025); border-radius: 18px; padding: clamp(18px, 2.4vw, 28px);
  display: grid; grid-template-columns: 1fr 1.9fr; gap: clamp(18px, 2.6vw, 28px); align-items: center;
  @media (max-width: 1000px) { grid-template-columns: 1fr; }
`;
const FacilityName = styled.div`
  display: grid; gap: 8px;
  .back { font-family: 'JetBrains Mono', monospace; font-size: 10px; letter-spacing: 0.12em; color: ${MINT}; }
  .name { font-family: 'Fraunces', Georgia, serif; font-weight: 300; font-size: clamp(1.4rem, 2.4vw, 2rem); line-height: 1.1; }
  .id { font-size: 12px; opacity: 0.55; line-height: 1.5; }
`;
const Tiles = styled.div`
  display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 10px;
  @media (max-width: 640px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
`;
const Tile = styled.div<{ $hot?: boolean }>`
  background: rgba(244,239,231,0.035); border: 1px solid ${p => (p.$hot ? MINT : T.dark.line)}; border-radius: 10px; padding: 13px 15px; display: grid; gap: 5px; min-width: 0;
  .k { font-family: 'JetBrains Mono', monospace; font-size: 9px; letter-spacing: 0.12em; text-transform: uppercase; opacity: ${p => (p.$hot ? 1 : 0.55)}; color: ${p => (p.$hot ? MINT : 'inherit')}; }
  .v { font-family: 'Fraunces', Georgia, serif; font-size: 19px; line-height: 1.12; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .v.sm { font-size: 15px; white-space: normal; }
  .s { font-size: 11px; opacity: 0.55; } .s.mint { color: ${MINT}; opacity: 1; }
`;
const tiles: Array<{ k: string; v: string; s: string; sm?: boolean; mint?: boolean; hot?: boolean }> = [
  { k: 'Active contract', v: 'Version 2', s: 'Effective Jun 30, 2026' },
  { k: 'Latest released tape', v: 'Sep 30, 2026', s: '711 loans · released' },
  { k: 'Approved borrowing base', v: '$22,946,079', s: 'Approved Oct 7, 2026', hot: true },
  { k: 'Official exposure', v: '$17,955,559', s: 'Book of record' },
  { k: 'Availability', v: '$0', s: 'Held by 1 control · $4,990,520 before', mint: true },
  { k: 'Current exceptions', v: '1', s: '1 blocking' },
  { k: 'Next best action', v: 'Refresh the base approval', s: 'A checker’s decision', sm: true },
  { k: 'Official mark', v: '99.00%', s: '$17,776,004 market value' },
];

/* ------------------------------------------------------------- the month you have now */
const Lines = styled.div`
  display: grid; gap: 14px; font-family: 'Fraunces', Georgia, serif; font-weight: 300; font-size: clamp(1.3rem, 2.3vw, 1.75rem); line-height: 1.25;
  code { font-family: 'JetBrains Mono', monospace; font-size: 0.6em; letter-spacing: 0; padding: 2px 6px; border: 1px solid ${T.dark.line}; border-radius: 6px; vertical-align: middle; }
  .verdict { margin-top: 8px; color: ${MINT}; font-size: 1.15em; }
`;
const nowLines: React.ReactNode[] = [
  'The certificate is taken on faith.',
  <>The base is in <code>abf_borrowing_base_FINAL_v7.xlsx</code>.</>,
  'The draw was approved in a reply-all.',
  'The waterfall was checked by hand on the 15th.',
  'The report went out. Nobody knows if it arrived.'
];

/* ------------------------------------------------------------- before and after */
const Table = styled.div`
  display: grid; border-top: 1px solid rgba(22,21,15,0.3);
  .hd, .row { display: grid; grid-template-columns: 0.5fr 1fr 1.25fr; gap: 24px; padding: 18px 0; border-bottom: 1px solid ${T.light.line}; align-items: start; }
  .hd { padding: 12px 0; font-family: 'JetBrains Mono', monospace; font-size: 9px; letter-spacing: 0.12em; text-transform: uppercase; opacity: 0.55; }
  .stage { font-family: 'Fraunces', Georgia, serif; font-size: 22px; line-height: 1.1; }
  .today { font-size: 14px; line-height: 1.55; color: ${T.light.sub}; }
  .after { font-size: 14px; line-height: 1.55; }
  .after b { font-weight: 600; } .after b.mint { color: ${MINT_INK}; }
  @media (max-width: 760px) {
    .hd { display: none; }
    .row { grid-template-columns: 1fr; gap: 8px; }
    .today::before { content: 'Today · '; font-family: 'JetBrains Mono', monospace; font-size: 10px; letter-spacing: 0.1em; text-transform: uppercase; }
    .after::before { content: 'With AxiomFlow · '; font-family: 'JetBrains Mono', monospace; font-size: 10px; letter-spacing: 0.1em; text-transform: uppercase; color: ${MINT_INK}; }
  }
`;
const stages: Array<{ id: string; stage: string; today: string; after: React.ReactNode }> = [
  { id: 'tape', stage: 'Tape', today: 'Opened in Excel, filtered by eye, exclusions by memory.',
    after: <><b>711 loans</b> validated against the agreement’s rules. Every exclusion carries the rule that excluded it.</> },
  { id: 'base', stage: 'Base', today: 'The borrower’s certificate, retyped into the sheet.',
    after: <>Recalculated from the pool and compared line by line. <b>$22,946,079</b> approved by a second person; a <b>$57,365</b> tolerance applied, not assumed.</> },
  { id: 'draw', stage: 'Draw', today: 'Availability from last month’s sheet; the covenant checked later.',
    after: <><b>$4,990,520</b> before holds, <b className="mint">$0</b> drawable. One covenant holds it, named on the screen.</> },
  { id: 'cash', stage: 'Cash', today: 'Servicer report against the bank statement, by hand.',
    after: <>Tape, servicer and bank side by side: <b>$1,185,380.81</b> on all three. Timing items kept apart from breaks.</> },
  { id: 'close', stage: 'Close', today: 'The agent’s statement eyeballed; a total that agrees is good enough.',
    after: <>Checked step by step. Two steps differ by <b>$1,150</b>; each has an owner and a decision before sign-off.</> },
  { id: 'report', stage: 'Report', today: 'Emailed. The acknowledgement, if any, is in an inbox.',
    after: <>Certified by a second person and frozen with a hash. Delivered to the administrator and the trustee; <b>both acknowledgements</b> on record.</> },
];

/* ------------------------------------------------------------- how it fits */
const Cards = styled.div`
  display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 16px;
  @media (max-width: 860px) { grid-template-columns: 1fr; }
`;
const Card = styled.div`
  background: rgba(255,252,246,0.55); border: 1px solid ${T.light.line}; border-radius: 16px; padding: 24px; display: grid; gap: 10px; align-content: start;
  .k { font-family: 'JetBrains Mono', monospace; font-size: 10px; letter-spacing: 0.12em; text-transform: uppercase; color: ${MINT_INK}; }
  h3 { margin: 0; font-family: 'Fraunces', Georgia, serif; font-weight: 400; font-size: 1.45rem; line-height: 1.15; }
  p { margin: 0; font-size: 14px; line-height: 1.55; color: ${T.light.sub}; }
`;
const fits = [
  { k: 'Runs alongside', h: 'Your OMS keeps trading. Your administrator keeps the books.', p: 'AxiomFlow holds the facility’s record and hands the administrator a reconciled package each month.' },
  { k: 'One facility first', h: 'One agreement, one tape, one month.', p: 'The agreement is read into terms with their sources. The first tape lands. The first month closes with a record.' },
  { k: 'Grows into one book', h: 'When you’re ready, the same record carries the rest.', p: 'Loans, bonds, CLO and private credit post to the same book the facility already lives in.' },
];

/* ------------------------------------------------------------- proof */
const Proof = styled.div`
  display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 16px;
  @media (max-width: 860px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  > div { display: grid; gap: 8px; padding-top: 16px; border-top: 1px solid rgba(244,239,231,0.26); align-content: start; }
  .n { font-family: 'Fraunces', Georgia, serif; font-weight: 300; font-size: clamp(2rem, 3.4vw, 2.75rem); line-height: 1; }
  .n.mint { color: ${MINT}; }
  .c { font-size: 13px; line-height: 1.5; color: ${T.dark.sub}; }
`;
const proof = [
  { n: '$0', mint: true, c: 'drawable, held by one named covenant. $4,990,520 before the hold.' },
  { n: '$1.19M', c: 'of collections tied across tape, servicer and bank, to the cent.' },
  { n: '$1,150', c: 'waterfall difference found on two steps, each dispositioned before sign-off.' },
  { n: '2 of 2', c: 'recipients acknowledged the certified, hash-frozen report.' },
];

/* ------------------------------------------------------------- reveal */
const Reveal: React.FC<{ children: React.ReactNode; delay?: number; className?: string; style?: React.CSSProperties }> = ({ children, delay = 0, className, style }) => {
  const reduced = !!useReducedMotion();
  return (
    <motion.div className={className} style={style}
      initial={reduced ? false : { opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.25 }}
      transition={{ duration: 0.75, ease: EASE, delay }}>
      {children}
    </motion.div>
  );
};

/* ------------------------------------------------------------- page */
const ABFLandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, logout } = useAuth();
  const enter = useCallback(() => navigate(isAuthenticated ? '/home' : '/login'), [navigate, isAuthenticated]);
  const [contactOpen, setContactOpen] = useState(false);
  const openContact = useCallback((e?: React.SyntheticEvent) => { e?.preventDefault(); setContactOpen(true); }, []);
  const closeContact = useCallback(() => setContactOpen(false), []);

  useEffect(() => {
    const oldTitle = document.title; document.title = 'AxiomFlow ABF | Start with one facility';
    window.scrollTo(0, 0);
    return () => { document.title = oldTitle; };
  }, []);

  return (
    <Root>
      <Global />
      <Header>
        <Brand href={HOME} onClick={e => { e.preventDefault(); navigate(HOME); }}><AxiomFlowMark size={28} /><span>AxiomFlow</span></Brand>
        <TopRight>
          {isAuthenticated && <button onClick={logout}><LogOut size={14} /> Sign out</button>}
          <QuietSm as="a" href={HOME} onClick={e => { e.preventDefault(); navigate(HOME); }}>← The platform</QuietSm>
          <QuietSm as="a" href={CONTACT} onClick={openContact}>Talk to us</QuietSm>
          <QuietSm onClick={enter}>{isAuthenticated ? 'Enter platform' : 'Sign in'}</QuietSm>
          {DEMO_BOOKING_ENABLED && <DemoSm href={CAL_URL} target="_blank" rel="noopener noreferrer">Book a demo <ArrowRight size={13} /></DemoSm>}
        </TopRight>
      </Header>

      {/* Hero: the promise, and the product as the image */}
      <Band $m="dark" style={{ paddingTop: 'clamp(28px, 5vw, 56px)' }}>
        <Inner>
          <Two $ratio="1.15fr .85fr" $align="end">
            <Stack $gap={22}>
              <Eyebrow>Asset-based finance</Eyebrow>
              <H1>Your borrowing base lives in a spreadsheet.<br /><span className="mint">It doesn’t have to.</span></H1>
              <Lede $m="dark">AxiomFlow ABF runs alongside your OMS and your fund administrator. One agreement, one tape, one month: live in weeks, not a platform migration.</Lede>
              <CtaRow>
                {DEMO_BOOKING_ENABLED
                  ? <Primary href={CAL_URL} target="_blank" rel="noopener noreferrer">Book a demo <ArrowRight size={16} /></Primary>
                  : <Primary href={CONTACT} onClick={openContact}>Email us <ArrowRight size={16} /></Primary>}
                <Ghost href="#fits">How it fits alongside</Ghost>
              </CtaRow>
            </Stack>
            <OnPage aria-label="On this page">
              <Eyebrow style={{ marginBottom: 4 }}>On this page</Eyebrow>
              <a href="#now"><b>01</b>The month you have now</a>
              <a href="#after"><b>02</b>Before and after, stage by stage</a>
              <a href="#fits"><b>03</b>How it fits alongside what you run</a>
              <a href="#proof"><b>04</b>What a reviewed month looks like</a>
            </OnPage>
          </Two>
          <Reveal>
            <Stack $gap={12}>
              <Facility aria-label="A facility header as the product shows it">
                <FacilityName>
                  <div className="back">← ABF FACILITIES</div>
                  <div className="name">Ridgeline Consumer Receivables Warehouse 2026-2</div>
                  <div className="id">ABF-RCR-2026-2 · Ridgeline Receivables SPV LLC<br />Consumer loans · Active</div>
                </FacilityName>
                <Tiles>
                  {tiles.map(t => (
                    <Tile key={t.k} $hot={t.hot}>
                      <div className="k">{t.k}</div>
                      <div className={`v ${t.sm ? 'sm' : ''}`}>{t.v}</div>
                      <div className={`s ${t.mint ? 'mint' : ''}`}>{t.s}</div>
                    </Tile>
                  ))}
                </Tiles>
              </Facility>
              <Fine>The facility header, as the product shows it · a sample $50M consumer-receivables warehouse · figures as recorded, not a customer result</Fine>
            </Stack>
          </Reveal>
        </Inner>
      </Band>

      {/* The month you have now */}
      <Band $m="dark" id="now" style={{ borderTop: `1px solid ${T.dark.line}` }}>
        <Inner>
          <Two $ratio=".9fr 1.1fr" $align="start">
            <Reveal>
              <Stack $gap={16}>
                <Eyebrow>The month you have now</Eyebrow>
                <H2>Five things that happen every month, and leave no record.</H2>
              </Stack>
            </Reveal>
            <Lines>
              {nowLines.map((ln, i) => (
                <Reveal key={i} delay={i * 0.08} style={{ opacity: 1 }}>
                  <div style={{ opacity: 1 - i * 0.17 }}>{ln}</div>
                </Reveal>
              ))}
              <Reveal delay={0.5}><div className="verdict">None of it has a record.</div></Reveal>
            </Lines>
          </Two>
        </Inner>
      </Band>

      {/* Before and after */}
      <Band $m="light" id="after">
        <Inner>
          <Reveal>
            <Stack $gap={12} style={{ maxWidth: '62ch' }}>
              <Eyebrow>Before and after</Eyebrow>
              <H2 $m="light">The same month, stage by stage.</H2>
              <Lede $m="light">Figures on the right are from a sample facility inside AxiomFlow, as the product recorded them.</Lede>
            </Stack>
          </Reveal>
          <Table>
            <div className="hd"><span>Stage</span><span>Today</span><span>With AxiomFlow</span></div>
            {stages.map((s, i) => (
              <Reveal key={s.id} delay={i * 0.05}>
                <div className="row" id={s.id} style={{ scrollMarginTop: 24 }}>
                  <span className="stage">{s.stage}</span>
                  <span className="today">{s.today}</span>
                  <span className="after">{s.after}</span>
                </div>
              </Reveal>
            ))}
          </Table>
        </Inner>
      </Band>

      {/* How it fits */}
      <Band $m="light" id="fits" style={{ paddingTop: 0 }}>
        <Inner>
          <Reveal>
            <Stack $gap={12} style={{ maxWidth: '62ch' }}>
              <Eyebrow>How it fits</Eyebrow>
              <H2 $m="light">Nothing to replace.</H2>
            </Stack>
          </Reveal>
          <Cards>
            {fits.map((f, i) => (
              <Reveal key={f.k} delay={i * 0.08}>
                <Card><div className="k">{f.k}</div><h3>{f.h}</h3><p>{f.p}</p></Card>
              </Reveal>
            ))}
          </Cards>
          <Reveal><Lede $m="light">Have a facility in mind? <a href={CONTACT} onClick={openContact} style={{ color: MINT_INK, fontWeight: 600, textDecoration: 'none' }}>{CONTACT_EMAIL}</a></Lede></Reveal>
        </Inner>
      </Band>

      {/* Proof */}
      <Band $m="dark" id="proof">
        <Inner>
          <Reveal>
            <Stack $gap={12} style={{ maxWidth: '62ch' }}>
              <Eyebrow>What a reviewed month looks like</Eyebrow>
              <H2>August, on one sample facility.</H2>
            </Stack>
          </Reveal>
          <Proof>
            {proof.map((p, i) => (
              <Reveal key={p.n} delay={i * 0.08}>
                <div><div className={`n ${p.mint ? 'mint' : ''}`}>{p.n}</div><div className="c">{p.c}</div></div>
              </Reveal>
            ))}
          </Proof>
          <Fine>Sample facility · not a customer result</Fine>
        </Inner>
      </Band>

      {/* Close */}
      <Band $m="dark" style={{ borderTop: `1px solid ${T.dark.line}`, textAlign: 'center' }}>
        <Inner style={{ justifyItems: 'center', gap: 20 }}>
          <Reveal>
            <Stack $gap={18} style={{ justifyItems: 'center' }}>
              <H2 style={{ fontSize: 'clamp(2.2rem, 4.6vw, 3.6rem)', maxWidth: '16ch' }}>See your facility <span className="mint">in it.</span></H2>
              <Lede $m="dark" style={{ textAlign: 'center' }}>Bring one agreement and one tape. We’ll walk the month with you.</Lede>
              <CtaRow style={{ justifyContent: 'center' }}>
                {DEMO_BOOKING_ENABLED
                  ? <Primary href={CAL_URL} target="_blank" rel="noopener noreferrer">Book a demo <ArrowRight size={16} /></Primary>
                  : <Primary href={CONTACT} onClick={openContact}>Email us <ArrowRight size={16} /></Primary>}
                <Ghost as="button" onClick={enter}>{isAuthenticated ? 'Enter platform' : 'Sign in'}</Ghost>
                <Ghost href={HOME} onClick={e => { e.preventDefault(); navigate(HOME); }}>The whole platform</Ghost>
              </CtaRow>
              <Fine style={{ textTransform: 'none', opacity: 0.6 }}>{DEMO_BOOKING_ENABLED ? 'or email ' : ''}<a href={CONTACT} onClick={openContact} style={{ color: 'inherit' }}>{CONTACT_EMAIL}</a></Fine>
            </Stack>
          </Reveal>
          <Fine style={{ display: 'flex', gap: 18, flexWrap: 'wrap', justifyContent: 'center' }}><span>Control · Clarity · Confidence</span><span style={{ textTransform: 'none', letterSpacing: '0.06em' }}>AxiomFlow is a product of AnjaniGiri.</span></Fine>
        </Inner>
      </Band>
      <ContactCard open={contactOpen} onClose={closeContact} subject={CONTACT_SUBJECT}
        title={<>Bring one facility. We’ll walk the month with you.</>}
        lede={<>Tell us the asset class, the lender and the size, or just ask a question. A line or two is enough to start.</>} />
    </Root>
  );
};

export default ABFLandingPage;
